import {Person} from "../../../services/interface/query/person";
import {useTranslation} from "react-i18next";
import React, {useEffect, useState} from "react";
import NotPermissionComponent from "../NotPermissionComponent";
import {useFindChecklistPeps} from "../../../services/apis/apiChecklist";
import {ChecklistPepDetailInterface} from "../../../services/interface/query/checklist-interface";
import NoRecords from "../../components/NoRecords";
import * as IconsMui from "@mui/icons-material";
import {IconButton, Collapse} from "@material-tailwind/react";
import CustomTable from "../../../components/general/CustomTable";

interface Props {
    person?: Person;
}

/**
 * Muestra registros PEPS (Personas Expuestas Políticamente) de la persona consultada.
 * Consume el endpoint POST /control_lists/peps.
 *
 * @author cfernandez
 * @version 1.0.0
 * @since 2026-04-08
 */
export default function ChecklistPeps({person}: Props) {
    const { t } = useTranslation("global") as { t: (key: string) => string };

    const {
        mutate: findPeps,
        data: successPeps,
        error: errorPeps,
        isPending: loadingPeps,
    } = useFindChecklistPeps()

    const [response, setResponse] = useState<ChecklistPepDetailInterface[] | null | undefined>()
    const [isOpen, setIsOpen] = React.useState(false);

    useEffect(() => {
        if (person?.identification) {
            setResponse(undefined)
            findPeps({identifier: person.identification, msj: t('peps')})
        }
    }, [person]);

    useEffect(() => {
        if (successPeps !== undefined) {
            setResponse(successPeps);
        }
    }, [successPeps]);

    const isResolved = response !== undefined && response !== null;
    const hasRecords = isResolved && response.length > 0;

    return (
        <NotPermissionComponent
            className={'min-h-[50px]'}
            title={'/pn_checklist'}
            loading={loadingPeps}
            error={errorPeps}
            component={
                <div>
                    <div className={'flex flex-row gap-3 !border border-transparent border-b-secondary pb-3'}>
                        {isResolved ?
                            !hasRecords ?
                                <IconsMui.EmojiEmotionsTwoTone className={'text-success'}/> :
                                <IconsMui.SentimentDissatisfiedTwoTone className={'text-danger'}/> :
                            <IconsMui.NotificationsOffTwoTone className={'text-empColorSecondary'}/>
                        }
                        {t('peps')}
                        <div className={'ml-auto flex flex-row gap-3 items-center'}>
                            {hasRecords &&
                                <div className={'text-[9px] font-light text-danger'}>{t('peps')}</div>}
                            {hasRecords &&
                                <IconButton placeholder="icon-button"
                                            size="sm"
                                            variant="text"
                                            onClick={() => setIsOpen((cur) => !cur)}
                                >
                                    <IconsMui.KeyboardArrowDownTwoTone className="h-6 w-6"/>
                                </IconButton>
                            }
                        </div>
                    </div>
                    {hasRecords ?
                        <Collapse open={isOpen} className="bg-white">
                            <CustomTable
                                classNameTable={'mt-2'}
                                header={[
                                    t('post'),
                                    t('cl-institution'),
                                    t('cl-source'),
                                    t('date'),
                                    t('observations'),
                                ]}
                                listElement={response?.map((item) => ({
                                    row: [
                                        item.position,
                                        item.institution,
                                        item.source,
                                        item.date,
                                        item.observations,
                                    ]
                                }))}
                            />
                        </Collapse>
                        : !loadingPeps && isResolved && <NoRecords/>
                    }
                </div>
            }
        />
    )
}
